import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel} from '@angular/forms';
import {ErrorStateMatcherDate} from '../../services/material/errorStateMatcherDate';
import {ErrorStateMatcher} from '@angular/material/core';
import {ErrorStateMatcherFailure} from '../../services/material/errorStateMatcherFailure';
import {MomentService} from '../../services/moment.service';
import {Constants} from '../constants';
import {TranslationService} from '../../services/translation.service';

@Component({
    selector: 'lv-material-date',
    templateUrl: './date.component.html',
    styleUrls: ['./date.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DateComponent),
        }
    ],
    standalone: false
})

export class DateComponent implements ControlValueAccessor, OnChanges {

  @Input('required')
  public isRequired: boolean;

  @Input('placeholder')
  public placeholder: string;

  @Input('showHint')
  public showHint: boolean;

  @Input('disabled')
  public disabled: boolean;

  @Input('name')
  public name: string = '';

  @Input('errorPath')
  public errorPath: string = '';

  @Input('textForRequiredError')
  public requiredErrorText: string;

  @Input('outerErrors')
  public outerErrors;

  @Input('startDate')
  public startDate: Date;

  @Input('autocomplete')
  public autocomplete: string;

  @Input()
  public meldung: any[];

  @Output('ngModel')
  public dateChange: EventEmitter<Date> = new EventEmitter<Date>();

  @Output()
  public blurEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('date', {static: true})
  private _date: NgModel;

  public dateMatcher: ErrorStateMatcher;
  public hasFocus: boolean;
  public showMeldung: boolean = false;
  public showUntruncatedError: boolean = false;
  private _value: Date;
  private _isDirty: boolean;
  private _isTouched: boolean;

  constructor(private _momentService: MomentService,
              private _translationService: TranslationService) {
  }

  public get model(): Date {
    return this._value;
  }

  public get isDirty(): boolean {
    return this._isDirty;
  }

  @Input('ngModel')
  public set model(value: Date) {
    if (value === this._value || !value && !this._value || this._isSame(value)) {
      return;
    }
    let date = this._momentService.moment(value, ['L', 'l'], true);
    if (!date.isValid()) {
      date = this._momentService.moment(value);
    }
    this._value = date.isValid() ? date.toDate() : value;
    this._onChange(this._value);
    this.dateChange.emit(this._value);
    this.showUntruncatedError = false;
  }

  @Input('isTouched')
  public set isTouched(value: boolean) {
    this._isTouched = value;
  }

  @Input('isDirty')
  public set isDirty(value: boolean) {
    this._isDirty = value;
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.getError();
  }

  public onBlur(): void {
    this.hasFocus = false;
    this.getError();
    this.blurEvent.emit(this._value);
  }

  public isError(): boolean {
    return (this.isDirty || this._date.dirty || this.isTouched || this._date.touched) &&
        (!!this._date.errors || !!this.outerErrors);
  }

  public writeValue(value: any): void {
    this._value = value;
  }

  public registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public getError(): string {
    this.dateMatcher = new ErrorStateMatcherFailure();
    if (this.isError() && this._date.errors?.PARTIAL) {
      const partialError = this._translationService.translate('ERRORS.DATUM.PARTIAL');
      return !this.hasFocus ? partialError : ' ';
    }
    if (this.isError() && this.outerErrors?.ERROR) {
      return this._translationService.translate('ERRORS.DATUM.' + this.outerErrors.ERROR);
    }
    if (this.isError() && this._date?.errors?.ERROR) {
      return this._translationService.translate('ERRORS.DATUM.' + this._date?.errors?.ERROR);
    }
    if (this.isError() && this.isRequired &&
        !this._date.errors?.PARTIAL && !this._date.errors?.ERROR && !this.outerErrors?.ERROR) {
      const requiredError = this._translationService.translate('ERRORS.REQUIRED');
      return !!this.requiredErrorText ? this.requiredErrorText : requiredError;
    }
    this._meldungAnzeigen();
    if (this.showMeldung) {
      return this.meldung[0].Meldungstext;
    }
    this.dateMatcher = new ErrorStateMatcherDate();
    return null;
  }

  public onErrorClick() {
    this.showUntruncatedError = !this.showUntruncatedError;
  }

  private _onChange = (_: any) => void 0;
  private _onTouched = () => void 0;

  private _isSame(newDate: any): boolean {
    if (!this._value) {
      return false;
    }
    const newMomentDate = this._momentService.moment(newDate);
    const oldMomentDate = this._momentService.moment(this._value);
    return newMomentDate.isSame(oldMomentDate);
  }

  private _meldungAnzeigen() {
    let show: boolean;
    switch (Constants.SHOW_MELDUNG_STATE) {
      case 'onTouched':
        show = !!this.meldung?.length && (this._isTouched || this._date.touched);
        break;
      case 'onDirty':
        show = !!this.meldung?.length && (this._isDirty || this._date.dirty);
        break;
      default:
        show = !!this.meldung?.length;
    }
    this.showMeldung = show;
  }
}
